import { AxiosInstance } from "axios";
import { IMessage, MessageStatus } from "../interfaces/IChatUser";

export interface SendMessageRequest {
    recipientUserId: string;
    content: string;
}

export interface SendMessageResponse {
    messageId: string;
    conversationId: string;
    sentAt: Date;
}

export interface ConversationMessage {
    messageId: string;
    conversationId: string;
    senderUserId: string;
    senderDisplayName: string;
    content: string;
    createdAt: string;
    isMine: boolean;
    isReadByOther: boolean;
    reactions?: {
        counts: Record<string, number>;
        userReaction?: string;
    };
}

export interface PagedMessagesResponse {
    items: ConversationMessage[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export interface ConversationListItem {
    conversationId: string;
    isGroup: boolean;
    otherUserId?: string;
    otherUserDisplayName?: string;
    lastMessageContent?: string;
    lastMessageTime?: string;
    lastMessageSenderName?: string;
    updatedAt?: string;
    unreadCount: number;
}

export interface PagedConversationsResponse {
    items: ConversationListItem[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export class ConversationService {
    private apiClient: AxiosInstance;

    constructor(apiClient: AxiosInstance) {
        this.apiClient = apiClient;
    }

    async getRecentConversations(page = 1, pageSize = 50): Promise<PagedConversationsResponse> {
        try {
            const response = await this.apiClient.get<PagedConversationsResponse>(
                '/participant/conversations',
                { params: { page, pageSize } }
            );
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to fetch conversations");
        }
    }

    async getMessages(conversationId: string, page = 1, pageSize = 50): Promise<IMessage[]> {
        try {
            const response = await this.apiClient.get<PagedMessagesResponse>(
                `/participant/conversations/${conversationId}/messages`,
                { params: { page, pageSize } }
            );

            const messages = response.data.items.map(msg => ({
                id: msg.messageId,
                senderId: msg.senderUserId,
                content: msg.content,
                timestamp: msg.createdAt,
                isRead: true,
                // Reconstruct the sender's tick from server read state so ticks
                // persist across reloads. Only our own messages carry a status;
                // received messages never show ticks.
                status: msg.isMine ? (msg.isReadByOther ? 'read' : 'sent') as MessageStatus : undefined,
                // Include reactions if present
                reactions: msg.reactions,
            })).reverse();

            return messages;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to fetch messages");
        }
    }

    async sendMessage(recipientUserId: string, content: string, clientMessageId?: string): Promise<SendMessageResponse> {
        try {
            const response = await this.apiClient.post<SendMessageResponse>(
                '/participant/conversations',
                {
                    recipientUserId,
                    content,
                    clientMessageId
                }
            );
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to send message");
        }
    }

    /**
     * Mark a conversation as read. This persists the read state on the server
     * so unread counts remain accurate across sessions and refreshes.
     */
    async markConversationAsRead(conversationId: string, lastReadMessageId?: string): Promise<void> {
        try {
            await this.apiClient.patch(
                `/participant/conversations/${conversationId}/mark-read`,
                null,
                {
                    params: lastReadMessageId ? { lastReadMessageId } : {}
                }
            );
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to mark conversation as read");
        }
    }

    /**
     * Add or update a reaction on a chat message
     */
    async addOrUpdateMessageReaction(messageId: string, reactionType: string): Promise<{ counts: Record<string, number>; userReaction?: string }> {
        try {
            const response = await this.apiClient.post(
                `/participant/conversations/messages/${messageId}/reactions`,
                { reactionType }
            );
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to add reaction");
        }
    }

    /**
     * Remove a reaction from a chat message
     */
    async removeMessageReaction(messageId: string): Promise<void> {
        try {
            await this.apiClient.delete(
                `/participant/conversations/messages/${messageId}/reactions`
            );
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to remove reaction");
        }
    }
}
